import type { FormField } from "../lib/formModel";

/**
 * One question, drawn the way the form asks it.
 *
 * Nothing here knows what an incident report is. It is handed a question from
 * whatever the API served and renders it by type, which is what lets a single
 * screen serve all three quality forms.
 */

interface Props {
    field: FormField;
    value: unknown;
    invalid: boolean;
    onChange: (value: unknown) => void;
}

/** Types the wizard cannot draw yet, and what the reporter should be told. */
const NOT_YET: Record<string, string> = {
    sketchPad: "Body diagram — mark injuries on the printed form for now and describe them in the notes below.",
    file: "Attachments cannot be added on this screen yet. Email them quoting the reference this report is given.",
    datagrid: "Repeating rows are not on this screen yet. Record them in the nearest description field.",
    editgrid: "Repeating rows are not on this screen yet. Record them in the nearest description field.",
    location: "Map pin is not on this screen yet. The written location above is what the register uses.",
    container: "Grouped answers are not on this screen yet.",
    submissionNumber: "Issued automatically when the report is filed.",
};

/** An address is one answer made of parts, so it is stored as one object. */
const ADDRESS_PARTS: Array<{ key: string; label: string; half: boolean }> = [
    { key: "line1", label: "Street address", half: false },
    { key: "line2", label: "Address line 2", half: false },
    { key: "suburb", label: "Suburb", half: true },
    { key: "state", label: "State", half: true },
    { key: "postcode", label: "Postcode", half: true },
    { key: "country", label: "Country", half: true },
];

const asRecord = (value: unknown): Record<string, unknown> =>
    value !== null && typeof value === "object" && !Array.isArray(value)
        ? (value as Record<string, unknown>)
        : {};

/**
 * Short answers sit two to a row; anything with room to write, or several
 * choices to read, keeps the full width. Purely how the page breathes - a
 * 24-question page of full-width single-line inputs reads like a corridor.
 */
const FULL_WIDTH = new Set(["textarea", "radio", "selectboxes", "address"]);

export const FormQuestion = ({ field, value, invalid, onChange }: Props) => {
    const id = `q-${field.key}`;
    const options = field.options ?? [];
    const width = FULL_WIDTH.has(field.type) || NOT_YET[field.type] ? "" : "half";

    const label = (
        <label htmlFor={id}>
            {field.label || field.key}
            {field.required && <span className="req" aria-hidden="true"> *</span>}
        </label>
    );

    const help = field.description ? <span className="help">{field.description}</span> : null;

    if (NOT_YET[field.type]) {
        return (
            <div className="field">
                {label}
                <span className="chip mute">{NOT_YET[field.type]}</span>
                {help}
            </div>
        );
    }

    if (field.type === "address") {
        const parts = asRecord(value);
        return (
            <div className={`field ${invalid ? "invalid" : ""}`}>
                {label}
                <div className="form-grid">
                    {ADDRESS_PARTS.map((part) => (
                        <div key={part.key} className={`field ${part.half ? "half" : ""}`}>
                            <label htmlFor={`${id}-${part.key}`}>{part.label}</label>
                            <input
                                id={`${id}-${part.key}`}
                                type="text"
                                value={String(parts[part.key] ?? "")}
                                onChange={(e) => onChange({ ...parts, [part.key]: e.target.value })}
                            />
                        </div>
                    ))}
                </div>
                {help}
            </div>
        );
    }

    if (field.type === "selectboxes") {
        // Several answers to one question, stored the way the form stores them:
        // a key per choice, true when ticked.
        const ticked = asRecord(value);
        return (
            <div className={`field ${invalid ? "invalid" : ""}`}>
                {label}
                <div className="chip-picker">
                    {options.map((option) => {
                        const on = ticked[option.value] === true;
                        return (
                            <button
                                type="button"
                                key={option.value}
                                className={`chip-toggle ${on ? "on" : ""}`}
                                aria-pressed={on}
                                onClick={() => onChange({ ...ticked, [option.value]: !on })}
                            >
                                {option.label}
                            </button>
                        );
                    })}
                </div>
                {help}
            </div>
        );
    }

    if (field.type === "radio") {
        return (
            <div className={`field ${invalid ? "invalid" : ""}`}>
                <span className="legend">
                    {field.label || field.key}
                    {field.required && <span className="req" aria-hidden="true"> *</span>}
                </span>
                <div className="chip-picker" role="radiogroup" aria-label={field.label || field.key}>
                    {options.map((option) => (
                        <button
                            type="button"
                            key={option.value}
                            className={`chip-toggle ${value === option.value ? "on" : ""}`}
                            role="radio"
                            aria-checked={value === option.value}
                            onClick={() => onChange(option.value)}
                        >
                            {option.label}
                        </button>
                    ))}
                </div>
                {help}
            </div>
        );
    }

    if (field.type === "checkbox") {
        return (
            <div className={`field ${width}`}>
                <label className="check">
                    <input
                        id={id}
                        type="checkbox"
                        checked={value === true}
                        onChange={(e) => onChange(e.target.checked)}
                    />
                    <span>{field.label || field.key}</span>
                </label>
                {help}
            </div>
        );
    }

    if (field.type === "select") {
        return (
            <div className={`field ${width} ${invalid ? "invalid" : ""}`}>
                {label}
                <select id={id} value={String(value ?? "")} aria-invalid={invalid}
                    onChange={(e) => onChange(e.target.value)}>
                    <option value="">—</option>
                    {options.map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                </select>
                {help}
            </div>
        );
    }

    if (field.type === "textarea") {
        return (
            <div className={`field ${invalid ? "invalid" : ""}`}>
                {label}
                <textarea id={id} value={String(value ?? "")} aria-invalid={invalid}
                    onChange={(e) => onChange(e.target.value)} />
                {help}
            </div>
        );
    }

    const inputType =
        field.type === "datetime" ? "datetime-local"
            : field.type === "day" ? "date"
                : field.type === "time" ? "time"
                    : field.type === "number" || field.type === "currency" ? "number"
                        : field.type === "email" || field.type === "user" ? "email"
                            : field.type === "phoneNumber" ? "tel"
                                : "text";

    return (
        <div className={`field ${width} ${invalid ? "invalid" : ""}`}>
            {label}
            <input id={id} type={inputType} value={String(value ?? "")} aria-invalid={invalid}
                onChange={(e) => onChange(e.target.value)} />
            {field.type === "signature" && (
                <span className="help">Type your full name. Your account and the time are recorded with it.</span>
            )}
            {help}
        </div>
    );
};
